import React, { useEffect, useState } from 'react'
import PageHeader from '../components/PageHeader'
import PageBodyContainer from '../components/PageBodyContainer'
import TagsCreateModal from './TagsCreateModal'
import SegmentService from 'redux/config/services/SegmentService'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import Text from 'core-system/Text'
import { AppState } from 'redux/config/store'
import { useDispatch, useSelector } from 'react-redux'
import { Tag } from 'redux/tags/tagsTypes'
import DropdownV2 from 'core-system/Dropdown/DropdownV2'
import { SelectItemProps } from 'core-system/Dropdown'
import OverflowIcon from 'core-system/Icons/Misc/Overflow'
import EmployeesIcon from 'core-system/Icons/Misc/Employees'
import FlexContainer from 'core-system/FlexContainer'
import styled from 'styled-components'
import TagInfoDrawer from './TagInfoDrawer'
import SegmentOperations from '../worksites/SegmentOperations'
import Loading from 'core-system/Loading'
import { tagsActions } from 'redux/tags/tagsSlice'
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'core-system/Modal'
import TextField from 'core-system/TextField'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SegmentsDisplay from 'features/Programs/Shared/components/SegmentsDisplay'
import {
  getAllProgramForColumn,
  getProgramNamesForTagDeletion,
} from '../helperFunctions'
import palette from 'core-system/Themes/palette'

const Alias = styled.div`
  ${(props) => props.theme.typography.action4}
  color: ${(props) => props.theme.palette.text.primary};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const TopContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 1.5rem;
`

const getTableRows = (
  allTags: Tag[],
  menuCallback: (item: SelectItemProps) => void,
  onRowClick: (tag: Tag) => void,
  allFlexPrograms: object,
  allGrhPrograms: object,
  allQtfPrograms: object,
  allMicromobilityPrograms: object
) => {
  return allTags.map((tag: Tag) => {
    const programs = getAllProgramForColumn(
      allFlexPrograms,
      allGrhPrograms,
      allQtfPrograms,
      allMicromobilityPrograms,
      tag.id
    )
    const selectItems = [
      { id: '0', text: 'Edit Tag', tag },
      { id: '1', text: 'Delete Tag', tag },
    ]
    return (
      <TableRow key={tag.id} isClickable>
        <TableRowCell onClick={() => onRowClick(tag)}>
          <Alias>{tag.name}</Alias>
        </TableRowCell>
        <TableRowCell onClick={() => onRowClick(tag)}>
          <SegmentsDisplay
            segments={programs.map((program) => ({
              id: program,
              text: program,
              type: program,
            }))}
            displayType='table'
          />
        </TableRowCell>
        <TableRowCell onClick={() => onRowClick(tag)}>
          <FlexContainer alignItems='center'>
            <EmployeesIcon />
            {tag.commuters}
          </FlexContainer>
        </TableRowCell>
        <TableRowCell>
          <DropdownV2
            variant={'textual'}
            svgDropdown={true}
            usePortal={true}
            icon={<OverflowIcon />}
            active={null}
            items={selectItems}
            itemCallback={menuCallback}
            width={180}
          />
        </TableRowCell>
      </TableRow>
    )
  })
}

interface TagsViewProps {
  activeTab: number
  setActiveTab: (tab: number) => void
  tabs: { label: string; value: number }[]
}

const TagsView = ({ activeTab, setActiveTab, tabs }: TagsViewProps) => {
  const dispatch = useDispatch()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [selectedTag, setSelectedTag] = useState<Tag>(null)
  const [isTagInfoDrawerOpen, setIsTagInfoDrawerOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [editTagName, setEditTagName] = useState('')
  const [isInfoDrawerAddMode, setIsInfoDrawerAddMode] = useState(false)

  const allTags = useSelector((state: AppState) => state.tags.allTags)
  const allFlexPrograms = useSelector((state: AppState) => state.flex.flexPrograms)
  const allGrhPrograms = useSelector((state: AppState) => state.grh.grhPrograms)
  const allQtfPrograms = useSelector((state: AppState) => state.qtf.qtfPrograms)
  const allMicromobilityPrograms = useSelector(
    (state: AppState) => state.micromobility.micromobilityPrograms
  )
  const [tagsToRender, setTagsToRender] = useState<Tag[]>([])

  useEffect(() => {
    dispatch(tagsActions.getTags())
  }, [dispatch])

  useEffect(() => {
    if (allTags) {
      setTagsToRender(allTags)
    }
  }, [allTags])

  if (!allTags || !allFlexPrograms || !allGrhPrograms || !allQtfPrograms || !allMicromobilityPrograms) {
    return <Loading fullPage />
  }

  const openAddTagModal = () => {
    setIsCreateModalOpen(true)
    SegmentService.track('addTag-modal-click', { action: 'open' })
  }

  const closeAddTagModal = () => {
    setIsCreateModalOpen(false)
    SegmentService.track('addTag-modal-action', { action: 'close' })
  }

  const HeaderCell = (text: string) => (
    <TableRowCell>
      <Text fontWeight={600} variant={'action3'}>
        {text}
      </Text>
    </TableRowCell>
  )

  const headerCells = [
    HeaderCell('Tag'),
    HeaderCell('Programs'),
    HeaderCell('Commuters'),
    HeaderCell('Edit'),
  ]

  const menuCallback = (item: SelectItemProps) => {
    setSelectedTag(item.tag)
    if (item.id === '0') {
      setEditTagName(item.tag.name)
      setIsEditModalOpen(true)
    }
    if (item.id === '1') {
      setIsDeleteModalOpen(true)
    }
  }

  const onRowClick = (tag: Tag) => {
    setSelectedTag(tag)
    setIsInfoDrawerAddMode(false)
    setIsTagInfoDrawerOpen(true)
  }

  const handleEditTag = () => {
    if (!editTagName.trim() || !selectedTag) return
    dispatch(
      tagsActions.updateTag({ tagId: selectedTag.id, name: editTagName.trim() })
    )
    setIsEditModalOpen(false)
  }

  const handleDeleteTag = () => {
    if (!selectedTag) return
    dispatch(tagsActions.deleteTag({ tagId: selectedTag.id }))
    setIsDeleteModalOpen(false)
  }

  const programsCancelledOnDelete = selectedTag
    ? getProgramNamesForTagDeletion(
        selectedTag.id,
        allFlexPrograms,
        allGrhPrograms,
        allQtfPrograms,
        allMicromobilityPrograms
      )
    : []

  return (
    <>
      <PageHeader
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={tabs}
        newGroupWorksiteButtonText='Create New Tag'
        showCreateNewGroupButton
        createNewGroupWorksite={openAddTagModal}
      />
      <PageBodyContainer>
        <TopContainer>
          <SegmentOperations
            filters={[]}
            searchArray={['name']}
            segments={allTags}
            setFilteredSegments={setTagsToRender}
            sortKey='name'
          />
        </TopContainer>
        <Table
          headerCells={headerCells}
          rows={getTableRows(
            tagsToRender,
            menuCallback,
            onRowClick,
            allFlexPrograms,
            allGrhPrograms,
            allQtfPrograms,
            allMicromobilityPrograms
          )}
          stickyColumns={1}
        />
      </PageBodyContainer>
      <TagsCreateModal open={isCreateModalOpen} closeModal={closeAddTagModal} />
      {selectedTag && (
        <>
          <TagInfoDrawer
            open={isTagInfoDrawerOpen}
            closeModal={() => setIsTagInfoDrawerOpen(false)}
            tag={selectedTag}
            commutersAddMode={isInfoDrawerAddMode}
          />
          <Modal
            open={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            maxWidth='sm'
          >
            <ModalHeader
              title='Edit Tag'
              onClose={() => setIsEditModalOpen(false)}
            />
            <ModalBody>
              <TextField
                label='Tag Name'
                value={editTagName}
                onChange={(e) => setEditTagName(e.target.value)}
                fullWidth
                autoFocus
              />
            </ModalBody>
            <ModalFooter>
              <SecondaryButton
                onClick={() => setIsEditModalOpen(false)}
                style={{ marginRight: '1rem' }}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton
                onClick={handleEditTag}
                disabled={!editTagName.trim()}
              >
                Save
              </PrimaryButton>
            </ModalFooter>
          </Modal>
          <Modal
            open={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            maxWidth='sm'
          >
            <ModalHeader
              title='Delete Tag'
              onClose={() => setIsDeleteModalOpen(false)}
            />
            <ModalBody>
              <Text variant='body1'>
                Are you sure you want to delete the tag{' '}
                <strong>{selectedTag.name}</strong>? This will remove the tag
                from all commuters but will not delete any commuters.
              </Text>
              {programsCancelledOnDelete.length > 0 && (
                <Text
                  variant='body1'
                  marginTop='1rem'
                  color={palette.error.red600}
                >
                  This tag has programs ({programsCancelledOnDelete.join(', ')}
                  ). Deleting it will cancel those programs for all affected
                  commuters.
                </Text>
              )}
            </ModalBody>
            <ModalFooter>
              <SecondaryButton
                onClick={() => setIsDeleteModalOpen(false)}
                style={{ marginRight: '1rem' }}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={handleDeleteTag}>Delete</PrimaryButton>
            </ModalFooter>
          </Modal>
        </>
      )}
    </>
  )
}

export default React.memo(TagsView)
